import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useSyncExternalStore } from "react";
import { qk } from "../../lib/query-keys";
import { getOutbox } from "./outbox";

const RETRY_MS = 30_000;

/** Starts the replay loop once (app start, `online`, every 30 s while items wait), for this login's claims. */
export function useOutboxReplay(ownerId: string) {
  const qc = useQueryClient();
  useEffect(() => {
    const outbox = getOutbox();
    const run = async () => {
      if (!navigator.onLine) return;
      const res = await outbox.replay(ownerId).catch(() => undefined);
      if (res?.sent.length) await qc.invalidateQueries({ queryKey: qk.jobsAll });
      if (res?.signedOut) await qc.invalidateQueries({ queryKey: qk.me }); // sends the app to sign-in
    };
    void outbox.refresh().then(run);
    const onOnline = () => void run();
    window.addEventListener("online", onOnline);
    const timer = window.setInterval(() => {
      if (outbox.itemsFor(ownerId).length) void run();
    }, RETRY_MS);
    return () => {
      window.removeEventListener("online", onOnline);
      window.clearInterval(timer);
    };
  }, [qc, ownerId]);
}

/** Every saved claim on this phone, or only one login's. */
export function useOutboxItems(ownerId?: string) {
  const outbox = getOutbox();
  const all = useSyncExternalStore(outbox.subscribe, outbox.snapshot, outbox.snapshot);
  return useMemo(() => (ownerId ? all.filter((i) => !i.ownerId || i.ownerId === ownerId) : all), [all, ownerId]);
}

export function useOnline() {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener("online", cb);
      window.addEventListener("offline", cb);
      return () => {
        window.removeEventListener("online", cb);
        window.removeEventListener("offline", cb);
      };
    },
    () => navigator.onLine,
    () => true,
  );
}
